import { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { PtoVolumePosition } from '@shared/api';
import { IconChevronDown, IconChevronRight } from '@shared/ui/icons';
import { visibleColumns, type VolumeCellContext, type VolumeColumn } from './columns';
import { groupTree, nodeKeys, nodePathOf, type GroupNode } from './groups';
import { Facts, Grid, GroupTr, LinkButton, Toggle, ViewBar } from './systemsUi';
import { VolumeRow } from './VolumeRow';

/** Узлов не больше — раскрыты все уровни сразу; больше — только верхний. */
const OPEN_ALL_UP_TO = 12;

/** Отступ вложенного уровня: «> Сваи буронабивные СБм1» ▶ «Материалы». */
const LevelTd = styled.td<{ $depth: number }>`
  && {
    padding-left: ${({ theme, $depth }) => theme.spacing(2.5 + $depth * 6)};
  }
`;

function rowsWord(n: number): string {
  const d = n % 10;
  const dd = n % 100;
  if (d === 1 && dd !== 11) return 'строка';
  if (d >= 2 && d <= 4 && (dd < 12 || dd > 14)) return 'строки';
  return 'строк';
}

interface Props {
  positions: PtoVolumePosition[];
  ctx: VolumeCellContext;
  /** Позиция, открытая по ссылке на строку: её уровни раскрываются. */
  targetId: string | null;
}

/** Вид «По группам» (комплекты без систем; замечание ПТО 23.10, п. 3): вложенные
 *  сворачиваемые уровни пути группы таблицы («> Сваи буронабивные СБм1» ▶
 *  «Материалы») ▶ строки в порядке РД, с номерами и числом строк. Строки-
 *  заголовки групп строками не выводятся; строки без группы — «без группы»
 *  последней. */
export function GroupsTable({ positions, ctx, targetId }: Props) {
  const columns = visibleColumns(ctx);
  const tree = useMemo(() => groupTree(positions), [positions]);
  const all = useMemo(() => nodeKeys(tree), [tree]);
  const [open, setOpen] = useState<Set<string> | null>(null);
  const opened =
    open ?? new Set(all.length <= OPEN_ALL_UP_TO ? all : tree.map((n) => n.key));

  // Строка из ссылки — в раскрытых уровнях, в той же отрисовке (прокрутку к
  // строке делает экран). Один раз на строку.
  const [shownTarget, setShownTarget] = useState<string | null>(null);
  const targetPath = targetId ? nodePathOf(tree, targetId) : [];
  if (targetId && targetPath.length > 0 && shownTarget !== targetId) {
    setShownTarget(targetId);
    if (targetPath.some((k) => !opened.has(k))) setOpen(new Set([...opened, ...targetPath]));
  }

  function toggle(key: string) {
    const next = new Set(opened);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOpen(next);
  }

  const named = tree.filter((n) => n.key !== '').length;
  return (
    <>
      <ViewBar>
        <span>
          Групп таблиц: {named}
          {tree.some((n) => n.key === '') && ' · есть строки без группы'}
        </span>
        <LinkButton type="button" onClick={() => setOpen(new Set(all))}>
          Развернуть все
        </LinkButton>
        <LinkButton type="button" onClick={() => setOpen(new Set())}>
          Свернуть все
        </LinkButton>
      </ViewBar>
      <Grid>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.num ? 'num' : c.wrap ? 'wrap' : undefined}>
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tree.map((n) => (
            <GroupRows
              key={`grp:${n.key}`}
              node={n}
              opened={opened}
              onToggle={toggle}
              columns={columns}
              ctx={ctx}
              targetId={targetId}
            />
          ))}
        </tbody>
      </Grid>
    </>
  );
}

interface NodeProps {
  node: GroupNode<PtoVolumePosition>;
  opened: ReadonlySet<string>;
  onToggle: (key: string) => void;
  columns: VolumeColumn[];
  ctx: VolumeCellContext;
  targetId: string | null;
}

/** Уровень группы: строка-заголовок с числом строк, строки уровня и вложенные. */
function GroupRows({ node, opened, onToggle, columns, ctx, targetId }: NodeProps) {
  const isOpen = opened.has(node.key);
  return (
    <Fragment>
      <GroupTr>
        <LevelTd colSpan={columns.length} $depth={node.depth}>
          <Toggle type="button" aria-expanded={isOpen} onClick={() => onToggle(node.key)}>
            {isOpen ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            {node.key === '' ? 'Без группы таблицы' : node.label}
          </Toggle>
          <Facts>
            {node.count} {rowsWord(node.count)}
          </Facts>
        </LevelTd>
      </GroupTr>
      {isOpen && (
        <>
          {node.rows.map((p) => (
            <VolumeRow
              key={p.id}
              position={p}
              columns={columns}
              ctx={ctx}
              target={p.id === targetId}
            />
          ))}
          {node.children.map((c) => (
            <GroupRows
              key={`grp:${c.key}`}
              node={c}
              opened={opened}
              onToggle={onToggle}
              columns={columns}
              ctx={ctx}
              targetId={targetId}
            />
          ))}
        </>
      )}
    </Fragment>
  );
}
